import React from "react";
import { RegisterOptions, useController, useFormContext } from "react-hook-form";
import PicturePreview, { PicturePreviewHandle } from "./PicturePreview";
import useShowImage from "../../../../hooks/useShowImage";
import DocumentImageViewer from "./DocumentImageViewer";
import useImage from "./useImage";

export type FormPicturePreviewHandle = PicturePreviewHandle;

type FormPicturePreviewProps = {
  name: string;
  label?: string;
  onClick?: () => void;
  rules?: RegisterOptions;
  allowImageViewer?: boolean;
  allowClear?: boolean;
  disabled?: boolean;
  previewHeight?: number | string;
};

const FormPicturePreview = React.forwardRef<FormPicturePreviewHandle, FormPicturePreviewProps>(
  (
    {
      name,
      label = "Tomar foto",
      onClick,
      rules,
      allowImageViewer = false,
      allowClear = false,
      disabled = false,
      previewHeight,
    },
    ref
  ) => {
    const { control } = useFormContext();
    const { field } = useController({
      name,
      control,
      rules,
    });

    const getImage = useShowImage();
    const { uploadImage } = useImage();
    const [imageUrl, setImageUrl] = React.useState("");
    const [isViewerOpen, setIsViewerOpen] = React.useState(false);
    const picturePreviewRef = React.useRef<PicturePreviewHandle>(null);

    React.useImperativeHandle(ref, () => ({
      openCamera: () => {
        picturePreviewRef.current?.openCamera();
      },
    }));

    React.useEffect(() => {
      if (field.value instanceof File) {
        const fileUrl = URL.createObjectURL(field.value);
        setImageUrl(fileUrl);
        return () => URL.revokeObjectURL(fileUrl);
      } else if (typeof field.value === "string" && field.value) {
        setImageUrl(getImage(field.value));
        return undefined;
      }

      setImageUrl("");
      return undefined;
    }, [field.value, getImage]);

    const handleUpload = (file: File | null, dataUrl?: string) => {
      field.onChange(file || null);
      if (file && dataUrl) {
        uploadImage(name, file, dataUrl);
      }
    };

    const clearImage = () => {
      field.onChange(null);
      setImageUrl("");
    };

    return (
      <div data-field-name={name} style={{ width: "100%" }}>
        <PicturePreview
          ref={picturePreviewRef}
          label={label}
          imageUrl={imageUrl}
          onClick={onClick}
          onUpload={handleUpload}
          allowImageViewer={allowImageViewer}
          onImageViewerOpen={() => setIsViewerOpen(true)}
          allowClear={allowClear && !disabled}
          disabled={disabled}
          previewHeight={previewHeight}
        />
        <DocumentImageViewer
          open={isViewerOpen}
          onClose={() => setIsViewerOpen(false)}
          imageUrl={imageUrl}
          alt={label}
          fileName={field.value instanceof File ? field.value.name : label}
          allowDelete={allowClear && !disabled}
          onDelete={clearImage}
          onRetake={
            disabled
              ? undefined
              : () => {
                  setIsViewerOpen(false);
                  picturePreviewRef.current?.openCamera();
                }
          }
        />
      </div>
    );
  }
);

export default FormPicturePreview;
